"use client"

import { useCallback, useMemo, useState } from "react"
import { Household } from "@/schema"

import { useFetchCurrentClientIntegrations } from "@/hooks/api/current-client-integrations"
import { useFetchDialpadIntegrations } from "@/hooks/api/dialpad-integrations"
import { useElevateIntegrations } from "@/hooks/api/elevate-integrations/queries"
import { useFetchGoToIntegrations } from "@/hooks/api/go-to-integrations"
import { useFetchMyRepChatIntegrations } from "@/hooks/api/my-rep-chat-integrations"
import { useFetchRingCentralIntegrations } from "@/hooks/api/ring-central-integrations"
import { useFetchZoomPhoneIntegrations } from "@/hooks/api/zoom-phone-integrations"

import { RingoutCallDialog } from "./ringout-call-dialog"

interface OpenRingoutOptions {
  household?: Household
  preSelectedPhone?: string
  preSelectedExtension?: string | null
  preSelectedLabel?: string
}

export function useRingoutDialog() {
  const [options, setOptions] = useState<OpenRingoutOptions | null>(null)
  const { data: ccIntegrations } = useFetchCurrentClientIntegrations()
  const { data: rcIntegrations } = useFetchRingCentralIntegrations()
  const { data: mrcIntegrations } = useFetchMyRepChatIntegrations()
  const { data: dpIntegrations } = useFetchDialpadIntegrations()
  const { data: goToIntegrations } = useFetchGoToIntegrations()
  const { data: zoomIntegrations } = useFetchZoomPhoneIntegrations()
  const { data: elevateIntegrations } = useElevateIntegrations()

  const hasRingout = useMemo(() => {
    const hasRingoutIntegration =
      (ccIntegrations ?? []).some((i) => i.supports_ringout) ||
      (rcIntegrations ?? []).some((i) => i.is_current_user && i.supports_ringout) ||
      (mrcIntegrations ?? []).some((i) => i.supports_ringout) ||
      (dpIntegrations ?? []).some((i) => i.supports_ringout) ||
      (goToIntegrations ?? []).some((i) => i.is_current_user && i.supports_ringout) ||
      (elevateIntegrations ?? []).some((i) => i.is_current_user && i.supports_ringout)

    const hasTelDial = (rcIntegrations ?? []).some(
      (i) => i.is_current_user && i.configured && i.connected && !i.forwarding_number
    )

    const hasZoomDeepLink = (zoomIntegrations ?? []).some((i) => i.connected && (i.phone_numbers?.length ?? 0) > 0)

    return hasRingoutIntegration || hasTelDial || hasZoomDeepLink
  }, [
    ccIntegrations,
    rcIntegrations,
    mrcIntegrations,
    dpIntegrations,
    goToIntegrations,
    zoomIntegrations,
    elevateIntegrations,
  ])

  const open = useCallback((options: OpenRingoutOptions) => setOptions(options), [])
  const close = useCallback(() => setOptions(null), [])
  const dialog = options ? <RingoutCallDialog {...options} onClose={close} /> : null

  return { open, close, hasRingout, dialog }
}
